import React, { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { useNavigate, useParams } from 'react-router-dom'

import TextArea from "../../components/Inputs/TextArea/TextArea";
import InputEmoji from "../../components/Inputs/InputEmoji/Emoji";
import Label from "../../components/Label/Label";
import WishListTitle from "../../components/WishListTitle";
import Footer from "../../components/FooterButton";
import Spinner from "../../components/Spinner";

import { customBrandOpts } from "../../utils/customBrandSettings";
import MaisConteudoAPi from "../../services/MaisConteudoAPi";

import useWishList from "../../hooks/WishListContext";

import { Container, Form, InputLabelContainer, ErrorMessage } from '../CreateWishList/styles'
import { Input } from "../../components/Inputs/Input/InputStyle";
import { storedEnv } from "../../utils/localStorage";

interface IFormValuesState {
    name: string;
    brand: string;
    description: string;
    link: string;
    code: string;
    products: { productRef: string; }[]
}

interface IErrorState {
    shouldDisabledInput: boolean;
    error: string;
    isToShow: boolean;
}

const EditWishList: React.FC = () => {

    const inputEmojiRef = useRef<null | HTMLElement>(null)

    const mounted = useRef(false);

    const { setOpenedWishList, onOpenWishListHandler, onCloseWishListHandler } = useWishList()

    const navigate = useNavigate()
    const { linkId } = useParams()
    const customBrand = customBrandOpts.EditWishList[storedEnv]

    const [formValues, setFormValues] = useState<IFormValuesState>({
        brand: '',
        description: '',
        name: '',
        code: '',
        link: '',
        products: []
    })
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<IErrorState>({ error: '', shouldDisabledInput: false, isToShow: false })

    const onEmojiClickHandler = useCallback((event, emojiObject) => {
        setFormValues((prevState) => ({ ...prevState, 'name': `${prevState.name}${emojiObject.emoji}` }))
        inputEmojiRef.current?.focus()

    }, [])

    const onFetchWishlistHandler = useCallback(async () => {
        try {
            setLoading(true)
            const res = await MaisConteudoAPi.get(`/v1/wishlists/${linkId}`)

            if (mounted.current) {
                setFormValues({
                    brand: res.data.environment,
                    description: res.data.description,
                    name: res.data.name,
                    code: res.data.code,
                    link: res.data.linkName || '',
                    products: res.data.products
                })
                setLoading(false)
            }

        } catch (error: any) {
            setLoading(false)
            console.log(error.response?.data.message)
            setError({
                error: error.response?.data.message || "Algo de errado aconteceu, por favor tente novamente!",
                shouldDisabledInput: true,
                isToShow: true
            })
        }
    }, [linkId])

    const onInputChangeHandler = useCallback((value: string, name: string) => (
        setFormValues((prevState) => ({ ...prevState, [name]: value }))
    ), [])

    const formIsValidMemo = useMemo(() => {
        let isValid = true

        isValid = formValues.name?.trim().length > 0 && isValid
        isValid = formValues.brand?.trim().length > 0 && isValid

        return isValid
    }, [formValues])

    const ParamsInsertMemo = useMemo(() => (
        formValues.link.length > 0 ?
            {
                name: formValues.name,
                description: formValues.description,
                linkName: formValues.link
            }
            :
            {
                name: formValues.name,
                description: formValues.description,
            }

    ), [formValues.description, formValues.link, formValues.name])

    const onSubmitFormHandler = useCallback(async () => {
        if (!formIsValidMemo) return

        try {
            setLoading(true)
            const res = await MaisConteudoAPi.patch(`/v1/wishlists/${linkId}`, ParamsInsertMemo)
            setLoading(false)
            setOpenedWishList({
                action: 'edit',
                linkId: linkId || res.data.linkId,
                linkName: formValues.link || '',
                code: formValues.code,
                vtexBrandName: formValues.brand,
                name: formValues.name,
                description: formValues.description,
                products: []
            })

            onOpenWishListHandler()
            onCloseWishListHandler()

            !formValues.link ?
                navigate(`/my-wishlists/${formValues.brand}/${linkId}`)
                :
                navigate(`/my-wishlists/${formValues.brand}/${formValues.link || ''}/${formValues.code}/${linkId}`)


        } catch (error: any) {
            setLoading(false)
            setError({
                error: error.response?.data.message || "Algo de errado aconteceu, por favor tente novamente!",
                shouldDisabledInput: false,
                isToShow: true
            })
        }

    }, [formIsValidMemo, linkId, ParamsInsertMemo, setOpenedWishList, formValues.link, formValues.code, formValues.brand, formValues.name, formValues.description, onOpenWishListHandler, onCloseWishListHandler, navigate])

    useEffect(() => {
        mounted.current = true
        onFetchWishlistHandler()
        return () => { mounted.current = false }
    }, [onFetchWishlistHandler])

    return (
        <Container>
            {
                loading ? <Spinner />
                    :
                    <Form>
                        <WishListTitle className="h1__createWishlist--text">
                            Editar informações
                        </WishListTitle>

                        <InputLabelContainer>
                            <Label htmlFor="name" name="Nome da lojinha" />
                            <InputEmoji
                                id="name"
                                name="name"
                                notAutoComplete
                                onChange={(event) => onInputChangeHandler(event.target.value, event.target.name)}
                                onEmojiClickHandler={onEmojiClickHandler}
                                placeholder="Qual será o seu nome?"
                                value={formValues.name}
                                disabled={error.shouldDisabledInput || !customBrand.nomeDaLojinha()}
                                refInput={inputEmojiRef as any}
                            />
                        </InputLabelContainer>
                        {customBrand.linkDaLojinha &&
                            <InputLabelContainer>
                                <Label htmlFor="link" name="Link da lojinha" />
                                <Label htmlFor="nome" border={true}>mais-lojinha.somalabs.com/
                                    <Input
                                        id="link"
                                        name="link"
                                        value={formValues.link}
                                        onChange={(event) => onInputChangeHandler(event.target.value.replace(/[\s]/g, ""), event.target.name)}
                                        maxLength={20}
                                        InputLink={true}
                                    />
                                </Label>
                                <ErrorMessage className="errorMessage" showError={error.isToShow}>Nome já utilizado. </ErrorMessage>
                            </InputLabelContainer>
                        }
                        {customBrand.descricao ?
                            <InputLabelContainer>
                                <Label htmlFor="description" name="Descrição" secondName="(opcional)" />
                                <TextArea
                                    id="name"
                                    name="description"
                                    value={formValues.description}
                                    onTextAreaChange={(event) => onInputChangeHandler(event.target.value, event.target.name)}
                                    placeholder="Como você descreve essa lojinha?"
                                    disabled={error.shouldDisabledInput}
                                />
                            </InputLabelContainer> : null
                        }
                    </Form >
            }

            <Footer onClick={onSubmitFormHandler} name="Concluir" disabled={!formIsValidMemo || error.shouldDisabledInput} />
        </Container >
    )
}

export default EditWishList;